import React, { useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import { BLANK, DURING, FIRST_DETAIL, FIRST_QUESTION, KINDS, SHAPES, START, WATCHING, allDays, countLabel, detailOf, duringStep, extrasStep, finishStep, hasExtras, madeCount, piecesFrom, stageOf, tellStep, watchingStep, type DuringKey, type KindDetail, type KindKey, type SetupAnswers, type StageKey, type Watching } from '../../utils/clientSetup';
import { EXTRAS, extraDetailOf, extraSummary, extraTitle, type ExtraDetail, type ExtraKey } from '../../utils/clientExtras';
import { applySetup } from '../../utils/clientSetupWrite';
import { SetupChoice } from './SetupChoice';
import { SetupDays } from './SetupDays';
import { SetupKindFields } from './SetupKindFields';
import { SetupExtraFields } from './SetupExtraFields';
import { SetupScreen } from './SetupScreen';
import { SetupStart } from './SetupStart';
import { SetupDone } from './SetupDone';

/**
 * SETTING UP A NEW CLIENT PAGE.
 *
 * One thing at a time, a tick that is never left empty, and a finish that
 * hands you the link. See utils/clientSetup.ts for the screen order, and
 * SetupScreen.tsx for the grid every screen shares.
 *
 * **SOMETHING ELSE IS A LIST (16 Oct 2026).** Ticking it opens a short
 * pick-list, and SEVERAL CAN BE TICKED — Tom: *"there might be a
 * combination of these things, so give me the option to add all."* Each
 * one then gets its own screen, so films + cutdowns + a stream runs four
 * screens and the counter reads 1 of 3, 2 of 3, 3 of 3.
 *
 * The questions live in SetupKindFields.tsx and SetupExtraFields.tsx;
 * this file owns the ORDER and nothing else. It was at the size a single
 * safe overwrite can carry, which is why those two were lifted out.
 *
 * **THE CALL SHEET IS NOT A SETUP QUESTION (17 Oct 2026).** A call sheet
 * is not something a client sees on their page, and the OS already has a
 * tab that makes them. Tom: *"The call sheet is generated from that tab
 * and a link is sent directly to either a whatsapp group or emailed
 * directly. It is different from the client page."*
 *
 * **THE STUDIO IS TOLD WHEN THE SETUP HAS LANDED (17 Oct 2026).**
 *
 * `createProject` reloads the project list itself, with a comment saying
 * "read the stored rows back so the project page shows what exists". That
 * was right when creating a project meant creating an empty one. It is
 * wrong now: that reload fires the moment the PROJECT ROW lands, and
 * `applySetup` writes the deliverables, the shoot days and the tracker
 * seconds LATER. So the studio read a project that was genuinely empty at
 * that instant and then held that snapshot — a producer finished setup,
 * opened the job and found nothing on it, while the client page, which
 * caches nothing, showed all fourteen pieces.
 *
 * Tom found it the first time anyone walked this flow end to end. The
 * fix is the `reloadProjects()` below: one more read, AFTER the writes
 * rather than before them.
 *
 * It is awaited before `setBusy(false)`, so the button still reads
 * *Setting it up* while it happens rather than appearing to finish twice,
 * and it runs even when the setup reported trouble — a partial write is
 * exactly the case where the studio most needs to show what really saved.
 *
 * **`screenKey` is the step number** and SetupScreen keys its entrance
 * animation on it — never the title, which became editable text and made
 * the screen re-animate once per keystroke.
 *
 * **BACK GOES BACK THE WAY YOU CAME.** Every move pushes onto a trail and
 * Back pops it, rather than stepping one number down.
 */

const BIG = 'mt-4 w-full border-0 border-b border-paper/25 bg-transparent pb-3 font-display text-3xl font-light leading-none text-paper focus:border-paper focus:outline-none sm:text-4xl';
const FIELD = 'mt-3 w-full border-0 border-b border-paper/25 bg-transparent pb-2.5 text-[1.05rem] font-light text-paper placeholder:text-paper/20 focus:border-paper focus:outline-none';
const niceDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', {
  day: 'numeric',
  month: 'long'
});
export function NewProjectForm({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    createProject,
    findExistingProject,
    openProject,
    notify,
    reloadProjects
  } = usePortal();
  const [step, setStep] = useState(START);
  const [trail, setTrail] = useState<number[]>([]);
  const [answers, setAnswers] = useState<SetupAnswers>(BLANK);
  const [busy, setBusy] = useState(false);
  const [duplicate, setDuplicate] = useState<{
    id: string;
  } | null>(null);
  const [done, setDone] = useState<{
    slug: string;
    token: string;
    trouble?: string;
  } | null>(null);
  function go(next: number) {
    if (next === step) return;
    setTrail(current => [...current, step]);
    setStep(next);
  }
  function back() {
    if (trail.length > 0) {
      setStep(trail[trail.length - 1]);
      setTrail(trail.slice(0, -1));
      return;
    }
    setStep(Math.max(FIRST_QUESTION, step - 1));
  }
  const edit = (patch: Partial<SetupAnswers>) => setAnswers(current => ({
    ...current,
    ...patch
  }));
  const setDetail = (kind: KindKey, patch: Partial<KindDetail>) => setAnswers(current => ({
    ...current,
    details: {
      ...current.details,
      [kind]: {
        ...detailOf(current, kind),
        ...patch
      }
    }
  }));
  const setExtra = (key: ExtraKey, patch: Partial<ExtraDetail>) => setAnswers(current => ({
    ...current,
    extraDetails: {
      ...current.extraDetails,
      [key]: {
        ...extraDetailOf(current, key),
        ...patch
      }
    }
  }));
  const named = answers.client.trim().length > 0 && answers.name.trim().length > 0;
  /* KINDS order, not tick order, so the screens always run the same way. */
  const chosen = KINDS.filter(kind => kind.key !== 'other' && answers.kinds.includes(kind.key));
  const picked = EXTRAS.filter(extra => answers.extras.includes(extra.key));
  const during = duringStep(answers);
  const extrasAt = extrasStep(answers);
  const watch = watchingStep(answers);
  const tell = tellStep(answers);
  const finish = finishStep(answers);
  const who = answers.client.trim() || 'New client';
  const total = madeCount(answers);

  /** Every thing being made, in screen order — for the Continue label. */
  const madeTitles = [...chosen.map(kind => kind.label), ...picked.map(extra => extraTitle(extra.key, extraDetailOf(answers, extra.key)))];
  function toggleKind(key: KindKey) {
    const on = answers.kinds.includes(key);
    edit({
      kinds: on ? answers.kinds.filter(item => item !== key) : [...answers.kinds, key],
      details: on ? answers.details : {
        ...answers.details,
        [key]: detailOf(answers, key)
      }
    });
  }
  function toggleExtra(key: ExtraKey) {
    const on = answers.extras.includes(key);
    edit({
      extras: on ? answers.extras.filter(item => item !== key) : [...answers.extras, key],
      extraDetails: on ? answers.extraDetails : {
        ...answers.extraDetails,
        [key]: extraDetailOf(answers, key)
      }
    });
  }
  function toggleDuring(key: DuringKey) {
    edit({
      during: answers.during.includes(key) ? answers.during.filter(item => item !== key) : [...answers.during, key]
    });
  }
  function setContact(index: number, patch: Partial<{
    name: string;
    email: string;
  }>) {
    edit({
      contacts: answers.contacts.map((person, at) => at === index ? {
        ...person,
        ...patch
      } : person)
    });
  }
  async function create(force = false) {
    const client = answers.client.trim();
    const name = answers.name.trim();
    if (!client || !name) return;
    if (!force) {
      const existing = findExistingProject(client, name);
      if (existing) return setDuplicate({
        id: existing.id
      });
    }
    setBusy(true);
    createProject({
      client,
      name,
      type: '',
      templateId: '',
      showId: undefined
    });
    const result = await applySetup(answers);
    /* The studio's copy of this project was read before any of that was
       written. Read it again. See the note at the top of this file. */
    await reloadProjects();
    setBusy(false);
    setDuplicate(null);
    if (!result.slug) {
      notify(`${name} created — ${result.error ?? 'the setup did not save'}`);
      onClose();
      return;
    }
    setDone({
      slug: result.slug,
      token: result.token ?? '',
      trouble: result.ok ? '' : result.error
    });
  }
  if (done) {
    return <SetupDone client={answers.client.trim()} name={answers.name.trim()} slug={done.slug} token={done.token} trouble={done.trouble} onOpen={() => {
      const existing = findExistingProject(answers.client.trim(), answers.name.trim());
      onClose();
      if (existing) openProject(existing.id);
    }} onClose={onClose} data-id="element-1072" data-mp="7yasklolphwr" />;
  }
  if (step === START) {
    return <SetupStart onStart={() => setStep(FIRST_QUESTION)} onClose={onClose} data-id="element-1073" data-mp="3ge47wx774nc8" />;
  }
  const reached: StageKey[] = ['pre'];
  if (step >= during) reached.push('production');
  if (step > during) reached.push('post');
  const frame = {
    screenKey: step,
    answers,
    stage: stageOf(step, answers),
    reached,
    onStage: (key: StageKey) => go(key === 'pre' ? FIRST_QUESTION : key === 'production' ? during : watch),
    onJump: (target: string) => go(target === 'who' ? 1 : target === 'making' ? 2 : target === 'during' ? during : target === 'watching' ? watch : tell),
    onBack: back,
    onClose
  };
  if (step === 1) {
    return <SetupScreen {...frame} eyebrow="A new client" title="Who is it for?" canBack={trail.length > 0} canNext={named} onNext={() => go(2)} data-id="element-1074" data-mp="1d5ak37non5w9">
        <label className="block" data-id="element-1075" data-mp="1i3ltjha2fedy">
          <span className="label block text-paper/40" data-id="element-1076" data-mp="1mvdffeia0vxr">Client</span>
          <input value={answers.client} onChange={e => edit({
          client: e.target.value
        })} autoFocus className={BIG} data-id="element-1077" data-mp="1mxq40aiji4js" />
        </label>
        <label className="mt-12 block" data-id="element-1078" data-mp="uekjjpll3v6k">
          <span className="label block text-paper/40" data-id="element-1079" data-mp="udhuln7to2ta">Project title</span>
          <input value={answers.name} onChange={e => edit({
          name: e.target.value
        })} className={BIG} data-id="element-1080" data-mp="14m2otso09k1h" />
        </label>
      </SetupScreen>;
  }
  if (step === 2) {
    return <SetupScreen {...frame} eyebrow={who} title="What are we making?" canNext={answers.kinds.length > 0} onNext={() => go(FIRST_DETAIL)} onSkip={() => go(during)} data-id="element-1081" data-mp="1embmlaxnbdiz">
        <ul className="m-0 list-none p-0" data-id="element-1082" data-mp="lx4noaua4lx7">
          {KINDS.map(kind => <SetupChoice key={kind.key} label={kind.label} tag={kind.hint} pressed={answers.kinds.includes(kind.key)} onPick={() => toggleKind(kind.key)} data-id="element-1083" data-mp="qvpghp77c3bm" />)}
        </ul>
      </SetupScreen>;
  }

  /* One screen per main kind. */
  if (step >= FIRST_DETAIL && step < extrasAt && chosen.length > 0) {
    const index = Math.min(step - FIRST_DETAIL, chosen.length - 1);
    const kind = chosen[index];
    const detail = detailOf(answers, kind.key);
    return <SetupScreen {...frame} eyebrow={`${who} · ${index + 1} of ${total}`} title={kind.label} nextLabel={madeTitles[index + 1] ?? 'Continue'} hint={detail.shape === 'both' && kind.shaped ? 'Both makes two of each — a landscape cut and a vertical one.' : detail.ongoing ? 'More get added as they are made.' : ''} onNext={() => go(step + 1)} onSkip={() => go(step + 1)} data-id="element-1084" data-mp="2lfhma06bbxbe">
        <SetupKindFields spec={kind} detail={detail} onChange={patch => setDetail(kind.key, patch)} data-id="element-1085" data-mp="22gvhaa7odm27" />
      </SetupScreen>;
  }

  /* WHICH ONES — the Something else list. Several can be ticked. */
  if (step === extrasAt && hasExtras(answers)) {
    return <SetupScreen {...frame} eyebrow={who} title="Something else — what?" canNext={answers.extras.length > 0} hint="Tick as many as the job has. Each one gets its own screen next." onNext={() => go(step + 1)} onSkip={() => go(during)} data-id="element-1086" data-mp="1op7403lowrjn">
        <ul className="m-0 list-none p-0" data-id="element-1087" data-mp="1ert1z90ik5av">
          {EXTRAS.map(extra => <SetupChoice key={extra.key} label={extra.label} tag={extra.hint} pressed={answers.extras.includes(extra.key)} onPick={() => toggleExtra(extra.key)} data-id="element-1088" data-mp="152njufy9al0n" />)}
        </ul>
      </SetupScreen>;
  }

  /* One screen per ticked extra. */
  if (step > extrasAt && step < during && picked.length > 0) {
    const index = Math.min(step - extrasAt - 1, picked.length - 1);
    const extra = picked[index];
    const detail = extraDetailOf(answers, extra.key);
    const at = chosen.length + index;
    return <SetupScreen {...frame} eyebrow={`${who} · ${at + 1} of ${total}`} title={extraTitle(extra.key, detail)} nextLabel={madeTitles[at + 1] ?? 'Continue'} hint={extra.key === 'stream' ? detail.recording ? 'One recording per day, and the days go on their schedule.' : 'The days go on their schedule. Nothing to download afterwards.' : detail.shape === 'both' && extra.shaped ? 'Both makes two of each — a landscape cut and a vertical one.' : ''} onNext={() => go(step + 1)} onSkip={() => go(step + 1)} data-id="element-1089" data-mp="1f1l4h7sk9ued">
        <SetupExtraFields spec={extra} detail={detail} onChange={patch => setExtra(extra.key, patch)} data-id="element-1090" data-mp="9ubqh87n8hdx" />
      </SetupScreen>;
  }
  if (step === during) {
    return <SetupScreen {...frame} eyebrow={who} title="What do they see while we shoot?" onNext={() => go(watch)} onSkip={() => go(watch)} hint={answers.during.includes('plan') ? 'The tracker starts with five steps you can edit — it is never empty.' : ''} data-id="element-1091" data-mp="4xl2lyplkhhy">
        <ul className="m-0 list-none p-0" data-id="element-1092" data-mp="3naj9kpkt4alm">
          {DURING.map(item => <SetupChoice key={item.key} label={item.label} pressed={answers.during.includes(item.key)} onPick={() => toggleDuring(item.key)} data-id="element-1093" data-mp="3i9c10oaub7dw" />)}
        </ul>

        {/* A tick never stands alone: the days are asked for here. */}
        {answers.during.includes('dates') && <SetupDays days={answers.shoots} onDays={shoots => edit({
        shoots
      })} data-id="element-1094" data-mp="33a147su79av6" />}
      </SetupScreen>;
  }
  if (step === watch) {
    return <SetupScreen {...frame} eyebrow={who} title="How do they watch it?" onNext={() => go(tell)} data-id="element-1095" data-mp="3c8lfwa1cek9w">
        <ul className="m-0 list-none p-0" data-id="element-1096" data-mp="3h90rox5z7jpr">
          {WATCHING.map(item => <SetupChoice key={item.key} label={item.label} pressed={answers.watching === item.key} onPick={() => edit({
          watching: item.key as Watching
        })} data-id="element-1097" data-mp="3m7khx9rlrhnx" />)}
        </ul>
      </SetupScreen>;
  }
  if (step === tell) {
    const rows = answers.contacts.length > 0 ? answers.contacts : [{
      name: '',
      email: ''
    }];
    const write = (index: number, patch: Partial<{
      name: string;
      email: string;
    }>) => {
      if (index >= answers.contacts.length) {
        edit({
          contacts: [{
            name: '',
            email: '',
            ...patch
          }]
        });
      } else {
        setContact(index, patch);
      }
    };
    return <SetupScreen {...frame} eyebrow={who} title="Who should we email when a new cut is up?" nextLabel="See the summary" onSkip={() => go(finish)} onNext={() => go(finish)} hint={`The people at ${answers.client.trim() || 'the client'} who want to know. You can add and remove them any time.`} data-id="element-1098" data-mp="nvyyjfry5nrk">
        {rows.map((person, index) => <div key={index} className="mb-8 flex flex-wrap gap-x-8 gap-y-4" data-id="element-1099" data-mp="16ldzla7mucvy" data-mpi={index}>
            <label className="min-w-[10rem] flex-1" data-id="element-1100" data-mp="16ni0dja8ivs0">
              <span className="label-sm block text-paper/35" data-id="element-1101" data-mp="11nm0nw8klqi9">Name</span>
              <input value={person.name} className={FIELD} onChange={e => write(index, {
            name: e.target.value
          })} data-id="element-1102" data-mp="wvcaqg7ggqfz" />
            </label>
            <label className="min-w-[14rem] flex-[2]" data-id="element-1103" data-mp="su0kq9ko6379">
              <span className="label-sm block text-paper/35" data-id="element-1104" data-mp="1pcewlsp6zycv">Email</span>
              <input value={person.email} className={FIELD} onChange={e => write(index, {
            email: e.target.value
          })} data-id="element-1105" data-mp="1klgfws0aky93" />
            </label>
          </div>)}
        <button type="button" onClick={() => edit({
        contacts: [...rows, {
          name: '',
          email: ''
        }]
      })} className="label text-paper/45 transition-colors duration-200 hover:text-paper" data-id="element-1106" data-mp="3evddsqxc35ia">
          + Someone else
        </button>
      </SetupScreen>;
  }
  const pieces = piecesFrom(answers);
  const days = allDays(answers);
  return <SetupScreen {...frame} eyebrow={who} title="Here's what they'll get." nextLabel={busy ? 'Setting it up' : 'Create it'} canNext={!busy && named} onNext={() => void create()} data-id="element-1107" data-mp="2w1re5h7nj02k">
      {duplicate ? <div className="border border-paper/30 px-7 py-7" data-id="element-1108" data-mp="2m5wow38xa6qt">
          <p className="m-0 font-display text-2xl font-light leading-snug" data-id="element-1109" data-mp="2r3g8lioe3bq5">
            There is already a project with this client and name.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-6" data-id="element-1110" data-mp="ke1h3ypsemdv">
            <button type="button" onClick={() => {
          const id = duplicate.id;
          setDuplicate(null);
          onClose();
          openProject(id);
        }} className="label border border-paper bg-paper px-6 py-3.5 text-ink" data-id="element-1111" data-mp="pba4qetpfimi">
              Open the existing one
            </button>
            <button type="button" onClick={() => void create(true)} className="label text-paper/45 hover:text-paper" data-id="element-1112" data-mp="2jkp2v74vktj1">Make it anyway</button>
          </div>
        </div> : <>
          <dl className="m-0" data-id="element-1113" data-mp="2sk3o9s66qdwo">
            {[['Client', answers.client.trim() || '—'], ['Project title', answers.name.trim() || '—'], ...chosen.map(kind => {
          const detail = detailOf(answers, kind.key);
          const shaped = kind.shaped && !(kind.key === 'podcast' && detail.audioOnly);
          return [kind.label, [countLabel(detail, kind.pieces), kind.key === 'podcast' && detail.audioOnly ? 'audio only' : '', shaped && detail.shape !== 'landscape' ? SHAPES.find(s => s.key === detail.shape)?.label.toLowerCase() : '', detail.due ? `due ${niceDate(detail.due)}` : ''].filter(Boolean).join(' · ')];
        }), ...picked.map(extra => {
          const detail = extraDetailOf(answers, extra.key);
          return [extraTitle(extra.key, detail), [extraSummary(extra.key, detail), detail.due ? `due ${niceDate(detail.due)}` : ''].filter(Boolean).join(' · ')];
        }), ...(days.length ? [['Dates', days.length === 1 ? niceDate(days[0].date) + (days[0].location.trim() ? ` · ${days[0].location.trim()}` : '') : `${days.length} days from ${niceDate(days[0].date)}`]] : []), ['While we shoot', answers.during.length ? DURING.filter(i => answers.during.includes(i.key)).map(i => i.label).join(', ') : 'Nothing'], ['Watching', WATCHING.find(i => i.key === answers.watching)?.label ?? '—'], ['Emailing', answers.contacts.filter(c => c.email.trim()).length ? `${answers.contacts.filter(c => c.email.trim()).length} people` : 'Nobody yet']].map(([term, value]) => <div key={term} className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-1 border-b border-ruleDark py-4" data-id="element-1114" data-mp="2lit0vdiiwpxr">
                <dt className="label m-0 text-paper/40" data-id="element-1115" data-mp="2gk8as6wzjmn1">{term}</dt>
                <dd className="m-0 text-right text-[1.1rem] font-light" data-id="element-1116" data-mp="2bl57aqwnloh6">{value}</dd>
              </div>)}
          </dl>

          {/* The example is the placeholder, not a paragraph above the box:
              as standing text it read like an answer already filled in. */}
          <label className="mt-10 block" data-id="element-1117" data-mp="11n1tak3fpqpn">
            <span className="label block text-paper/40" data-id="element-1118" data-mp="11m09nr8crvwb">A line about it</span>
            <textarea value={answers.outline} rows={3} placeholder="A seven-part series on the people building Ras Al Khaimah." onChange={e => edit({
          outline: e.target.value
        })} className="mt-4 w-full resize-none border-0 border-b border-paper/25 bg-transparent pb-3 text-[1.05rem] font-light leading-relaxed text-paper placeholder:text-paper/20 focus:border-paper focus:outline-none" data-id="element-1119" data-mp="wnyqz7eu9yld" />
          </label>

          <p className="mt-6 m-0 text-[0.9rem] font-light text-paper/35" data-id="element-1120" data-mp="dztmfxqcy9li">
            {pieces.length} {pieces.length === 1 ? 'piece' : 'pieces'} to start with.
          </p>
        </>}
    </SetupScreen>;
}